// ts-rest contract for the Comply > Suitability page.
//
// Phase 4 of /api/db/:table removal. Composes:
//   - suitability_alerts rows
//   - wealth_vigil_findings rows (filtered to reviewStatus='pending')
//   - Client id->name map (for VIGIL client-name lookup)
// into one typed payload. The three mutations (acknowledge / escalate /
// issue-letter) become typed POSTs that delegate to the existing
// named-action handlers.

import { initContract } from '@ts-rest/core';
import { z } from 'zod';

import { staffActionResponses } from './agent';
import { clientStubSchema } from '../schemas/clients';

const c = initContract();

// -----------------------------------------------------------------------------
// SuitabilityAlert DTO (ADR-0003).
//
// The page renders the alert in a Comply display vocabulary the ontology row
// does not carry: spaced `alertType` labels ('Mandate Drift'), the four-state
// register `status` ('Action Required'), and a two-value `severity`
// ('Non-material'). The service maps the ontology enums onto these via total
// maps and enriches `clientName` (Client link). `investmentManager` /
// `dateRaised` are display fields the service derives — `dateRaised` from
// `raisedAt`. There is no ontology pre-image for an IM on the alert/client
// rows, so the alert DTO does not carry one (ADR-0003 read-side rule: do not
// fabricate domain state the ontology lacks).
// -----------------------------------------------------------------------------

export const suitabilityAlertTypeDtoSchema = z.enum([
    'Mandate Drift',
    'Concentration Risk',
    'Risk Profile Mismatch',
    'Benchmark Underperformance',
    'Income Shortfall',
    'Ethical Restriction Breach',
    'Capacity for Loss',
]);
export type SuitabilityAlertTypeDto = z.infer<typeof suitabilityAlertTypeDtoSchema>;

export const suitabilityAlertStatusDtoSchema = z.enum([
    'New',
    'Under Review',
    'Action Required',
    'Resolved',
]);
export type SuitabilityAlertStatusDto = z.infer<typeof suitabilityAlertStatusDtoSchema>;

export const suitabilityAlertSeverityDtoSchema = z.enum(['Material', 'Non-material']);
export type SuitabilityAlertSeverityDto = z.infer<typeof suitabilityAlertSeverityDtoSchema>;

export const suitabilityAlertDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    clientName: z.string(),
    alertType: suitabilityAlertTypeDtoSchema,
    description: z.string(),
    currentValue: z.string(),
    threshold: z.string(),
    /** Display date (en-GB) derived server-side from the ontology `raisedAt`. */
    dateRaised: z.string(),
    status: suitabilityAlertStatusDtoSchema,
    severity: suitabilityAlertSeverityDtoSchema,
    detail: z.string().nullable().optional(),
    /** Lineage to the originating VigilFinding when materialised; null for
     *  hand-raised alerts. */
    originFindingId: z.string().nullable().optional(),
});
export type SuitabilityAlertDto = z.infer<typeof suitabilityAlertDtoSchema>;

// -----------------------------------------------------------------------------
// VigilFinding DTO (ADR-0003) — the structured detector-finding envelope the
// shared <VigilFindingCard> glass-box renders. camelCase, mirroring the
// `VigilFinding` Object Type row (`vigil-finding.ts`). The `traceRef` block is
// a genuinely-dynamic agent version-stamp blob (model/prompt/ruleset/snapshot)
// — kept loose per ADR-0003's named exemption for glass-box trace payloads.
// -----------------------------------------------------------------------------

const vigilRuleSourceDtoSchema = z.object({
    kind: z.enum(['firm-policy', 'fca-handbook', 'mandate-clause', 'fund-investment-policy']),
    ref: z.string(),
    label: z.string(),
    url: z.string().nullable(),
});

const vigilEvaluationRowDtoSchema = z.object({
    condition: z.string(),
    observed: z.string(),
    threshold: z.string(),
    pass: z.boolean(),
    evidenceDimension: z.string().nullable(),
    driftDecomposition: z
        .object({ marketDrivenPct: z.number(), discretionaryPct: z.number() })
        .nullable(),
    note: z.string().nullable(),
});

const vigilAlternativeConclusionDtoSchema = z.object({
    conclusion: z.string(),
    whyRejected: z.string(),
});

export const vigilFindingDtoSchema = z.object({
    id: z.string(),
    clientId: z.string(),
    alertType: z.enum([
        'MandateDrift',
        'ConcentrationRisk',
        'RiskProfileMismatch',
        'BenchmarkUnderperformance',
        'IncomeShortfall',
        'EthicalRestrictionBreach',
        'CapacityForLossExceeded',
    ]),
    severity: z.enum(['Material', 'NonMaterial', 'PotentialMaterial']),
    ruleKey: z.string(),
    ruleStatement: z.string(),
    ruleSource: vigilRuleSourceDtoSchema,
    triggeredAt: z.string(),
    triggeredByKind: z.enum(['schedule', 'data', 'action', 'user']),
    triggeredByRef: z.string().nullable(),
    evaluation: z.array(vigilEvaluationRowDtoSchema),
    confidence: z.number(),
    confidenceGrounding: z.string(),
    alternativeConclusions: z.array(vigilAlternativeConclusionDtoSchema),
    counterfactualAnchor: z.string(),
    recommendedAction: z.object({ primary: z.string(), fallbacks: z.array(z.string()) }),
    priorFindingIds: z.array(z.string()),
    historicalContext: z.string(),
    materialisedAlertId: z.string().nullable().optional(),
    // Genuinely-dynamic glass-box version-stamp blob (ADR-0003 named exemption).
    traceRef: z.record(z.string(), z.unknown()).nullable().optional(),
    reviewStatus: z
        .enum(['pending', 'approved', 'rejected', 'revision_requested'])
        .nullable()
        .optional(),
    rejectionReason: z.string().nullable().optional(),
});
export type VigilFindingDto = z.infer<typeof vigilFindingDtoSchema>;

export const suitabilityResponseSchema = z.object({
    alerts: z.array(suitabilityAlertDtoSchema),
    pendingVigilFindings: z.array(vigilFindingDtoSchema),
    clientNames: z.array(clientStubSchema),
});
export type SuitabilityResponse = z.infer<typeof suitabilityResponseSchema>;

import { errorBody } from '../schemas/common';

export const complySuitabilityContract = c.router(
    {
        get: {
            method: 'GET',
            path: '/comply/suitability',
            responses: { 200: suitabilityResponseSchema, 403: errorBody },
            summary: 'Composed Comply > Suitability payload',
        },
        acknowledge: {
            method: 'POST',
            path: '/comply/suitability/:alertId/acknowledge',
            pathParams: z.object({ alertId: z.string() }),
            body: z.object({}).optional(),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Acknowledge a suitability alert',
        },
        escalate: {
            method: 'POST',
            path: '/comply/suitability/:alertId/escalate',
            pathParams: z.object({ alertId: z.string() }),
            // escalationReason is the officer's real reason for escalating, threaded
            // onto the EscalateSuitabilityAlert Action input (ADR-0003 write-side
            // rule, #167) — not a server constant.
            body: z.object({ escalationReason: z.string().min(1) }),
            responses: {
                200: z.object({ ok: z.literal(true) }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Escalate a suitability alert',
        },
        issueLetter: {
            method: 'POST',
            path: '/comply/suitability/:alertId/issue-letter',
            pathParams: z.object({ alertId: z.string() }),
            body: z.object({ clientName: z.string() }),
            responses: {
                200: z.object({ ok: z.literal(true), data: z.unknown().optional() }),
                400: errorBody,
                403: errorBody,
            },
            summary: 'Issue a SCRIVENER suitability letter for an alert',
        },

        // -----------------------------------------------------------------
        // VIGIL findings (OLD_CODE.html #2 replacement for the bespoke
        // `approve-vigil-finding` / `reject-vigil-finding` named actions
        // — different shape from the generic reviewable family).
        // -----------------------------------------------------------------
        approveVigilFinding: {
            method: 'POST',
            path: '/comply/vigil-findings/:id/approve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: staffActionResponses,
            summary: 'Approve a VIGIL finding (materialises a suitability alert).',
        },
        rejectVigilFinding: {
            method: 'POST',
            path: '/comply/vigil-findings/:id/reject',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ reason: z.string().optional() }).optional(),
            responses: staffActionResponses,
            summary: 'Reject a VIGIL finding.',
        },

        // -----------------------------------------------------------------
        // SCRIVENER suitability-letter-draft review. Bespoke materialise
        // effects (writes a durable SuitabilityLetter row) keep it outside
        // the generic reviewable family.
        // -----------------------------------------------------------------
        approveSuitabilityLetterDraft: {
            method: 'POST',
            path: '/comply/suitability-letter-drafts/:id/approve',
            pathParams: z.object({ id: z.string() }),
            body: z.object({}).optional(),
            responses: staffActionResponses,
            summary: 'Approve a SCRIVENER suitability-letter draft.',
        },
        rejectSuitabilityLetterDraft: {
            method: 'POST',
            path: '/comply/suitability-letter-drafts/:id/reject',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ reason: z.string().optional() }).optional(),
            responses: staffActionResponses,
            summary: 'Reject a SCRIVENER suitability-letter draft.',
        },
        requestRevisionSuitabilityLetterDraft: {
            method: 'POST',
            path: '/comply/suitability-letter-drafts/:id/request-revision',
            pathParams: z.object({ id: z.string() }),
            body: z.object({ revisionReason: z.string().min(1) }),
            responses: staffActionResponses,
            summary: 'Request a revision on a suitability-letter draft (ADR-0010).',
        },
    },
    { pathPrefix: '/api', strictStatusCodes: false },
);
